// client/src/coms/voip/voiceclient_parts/packets.js
import {
  DEFAULT_VOICE_ANIMATION,
  remoteSpeakingStateByIdentity,
  remoteVoiceAnimationByIdentity,
  remoteVoiceStatusByIdentity,
  SPEAKING_BROADCAST_MIN_MS,
  SPEAKING_PACKET_TOPIC,
  SPEAKING_PACKET_TYPE,
  SPEAKING_REMOTE_STALE_MS,
  VOICE_ANIMATION_PACKET_TOPIC,
  VOICE_ANIMATION_PACKET_TYPE,
  VOICE_ANIMATION_REMOTE_STALE_MS,
  VOICE_STATUS_PACKET_TOPIC,
  VOICE_STATUS_PACKET_TYPE,
  VOICE_STATUS_REMOTE_STALE_MS,
  voiceState,
} from './state';
import { decodeDataPayload, getLocalVoiceAnimation, normalizeVoiceAnimationValue } from './utils';

async function publishSpeakingPacket(payloadBytes) {
  const room = voiceState.activeRoom;
  const lp = room && room.localParticipant ? room.localParticipant : null;
  if (!lp || typeof lp.publishData !== 'function') return false;

  try {
    await lp.publishData(payloadBytes, { reliable: false, topic: SPEAKING_PACKET_TOPIC });
    return true;
  } catch {}

  try {
    await lp.publishData(payloadBytes, { reliable: false });
    return true;
  } catch {}

  try {
    await lp.publishData(payloadBytes);
    return true;
  } catch {}

  return false;
}

async function publishVoiceStatusPacket(payloadBytes) {
  const room = voiceState.activeRoom;
  const lp = room && room.localParticipant ? room.localParticipant : null;
  if (!lp || typeof lp.publishData !== 'function') return false;

  try {
    await lp.publishData(payloadBytes, { reliable: true, topic: VOICE_STATUS_PACKET_TOPIC });
    return true;
  } catch {}

  try {
    await lp.publishData(payloadBytes, { reliable: true });
    return true;
  } catch {}

  try {
    await lp.publishData(payloadBytes);
    return true;
  } catch {}

  return false;
}

async function publishVoiceAnimationPacket(payloadBytes) {
  const room = voiceState.activeRoom;
  const lp = room && room.localParticipant ? room.localParticipant : null;
  if (!lp || typeof lp.publishData !== 'function') return false;

  try {
    await lp.publishData(payloadBytes, { reliable: true, topic: VOICE_ANIMATION_PACKET_TOPIC });
    return true;
  } catch {}

  try {
    await lp.publishData(payloadBytes, { reliable: true });
    return true;
  } catch {}

  try {
    await lp.publishData(payloadBytes);
    return true;
  } catch {}

  return false;
}

export function setRemoteSpeakingState(identity, speaking, audioLevel) {
  const key = String(identity || '').trim();
  if (!key) return;

  remoteSpeakingStateByIdentity.set(key, {
    isSpeaking: !!speaking,
    audioLevel: Number.isFinite(Number(audioLevel)) ? Number(audioLevel) : 0,
    updatedAt: Date.now(),
  });
}

export function clearRemoteSpeakingState(identity) {
  const key = String(identity || '').trim();
  if (!key) return;
  remoteSpeakingStateByIdentity.delete(key);
}

export function clearAllRemoteSpeakingStates() {
  remoteSpeakingStateByIdentity.clear();
}

export function setRemoteVoiceStatus(identity, muted, deafened) {
  const key = String(identity || '').trim();
  if (!key) return;

  remoteVoiceStatusByIdentity.set(key, {
    muted: !!muted,
    deafened: !!deafened,
    updatedAt: Date.now(),
  });
}

export function clearRemoteVoiceStatus(identity) {
  const key = String(identity || '').trim();
  if (!key) return;
  remoteVoiceStatusByIdentity.delete(key);
}

export function clearAllRemoteVoiceStatuses() {
  remoteVoiceStatusByIdentity.clear();
}

export function setRemoteVoiceAnimation(identity, animation) {
  const key = String(identity || '').trim();
  if (!key) return;

  remoteVoiceAnimationByIdentity.set(key, {
    voiceAnimation: normalizeVoiceAnimationValue(animation),
    updatedAt: Date.now(),
  });
}

export function clearRemoteVoiceAnimation(identity) {
  const key = String(identity || '').trim();
  if (!key) return;
  remoteVoiceAnimationByIdentity.delete(key);
}

export function clearAllRemoteVoiceAnimations() {
  remoteVoiceAnimationByIdentity.clear();
}

function pruneRemoteSpeakingStates() {
  const now = Date.now();
  for (const [key, value] of remoteSpeakingStateByIdentity.entries()) {
    const updatedAt = Number(value && value.updatedAt);
    if (!Number.isFinite(updatedAt) || now - updatedAt > SPEAKING_REMOTE_STALE_MS) {
      remoteSpeakingStateByIdentity.delete(key);
    }
  }
}

function pruneRemoteVoiceStatuses() {
  const now = Date.now();
  for (const [key, value] of remoteVoiceStatusByIdentity.entries()) {
    const updatedAt = Number(value && value.updatedAt);
    if (!Number.isFinite(updatedAt) || now - updatedAt > VOICE_STATUS_REMOTE_STALE_MS) {
      remoteVoiceStatusByIdentity.delete(key);
    }
  }
}

function pruneRemoteVoiceAnimations() {
  const now = Date.now();
  for (const [key, value] of remoteVoiceAnimationByIdentity.entries()) {
    const updatedAt = Number(value && value.updatedAt);
    if (!Number.isFinite(updatedAt) || now - updatedAt > VOICE_ANIMATION_REMOTE_STALE_MS) {
      remoteVoiceAnimationByIdentity.delete(key);
    }
  }
}

export function getRemoteSpeakingState(identity) {
  pruneRemoteSpeakingStates();
  const key = String(identity || '').trim();
  if (!key) return null;
  const value = remoteSpeakingStateByIdentity.get(key);
  return value ? { ...value } : null;
}

export function getRemoteVoiceStatus(identity) {
  pruneRemoteVoiceStatuses();
  const key = String(identity || '').trim();
  if (!key) return null;
  const value = remoteVoiceStatusByIdentity.get(key);
  return value ? { ...value } : null;
}

export function getRemoteVoiceAnimation(identity) {
  pruneRemoteVoiceAnimations();
  const key = String(identity || '').trim();
  if (!key) return null;
  const value = remoteVoiceAnimationByIdentity.get(key);
  return value ? { ...value } : null;
}

export function broadcastLocalSpeakingState(force = false) {
  const now = Date.now();
  const speaking = !!voiceState.localSpeakingNow;
  const level = Number.isFinite(voiceState.localSpeakingLevel) ? voiceState.localSpeakingLevel : 0;

  const changedState = speaking !== voiceState.lastSpeakingBroadcastState;
  const changedLevel = Math.abs(level - voiceState.lastSpeakingBroadcastLevel) >= 0.025;
  const enoughTime = now - voiceState.lastSpeakingBroadcastAt >= SPEAKING_BROADCAST_MIN_MS;

  if (!force && !changedState && !changedLevel && !enoughTime) return;
  if (!force && !changedState && !enoughTime) return;

  const payload = JSON.stringify({
    t: SPEAKING_PACKET_TYPE,
    s: speaking ? 1 : 0,
    l: level,
    ts: now,
  });

  let bytes = null;
  try {
    bytes = new TextEncoder().encode(payload);
  } catch {
    return;
  }

  publishSpeakingPacket(bytes).catch(() => {});

  voiceState.lastSpeakingBroadcastAt = now;
  voiceState.lastSpeakingBroadcastState = speaking;
  voiceState.lastSpeakingBroadcastLevel = level;
}

export function broadcastLocalVoiceStatus(force = false) {
  const identity = String(voiceState.activeRoom?.localParticipant?.identity || '').trim();
  if (!identity && !force) return;

  const payload = JSON.stringify({
    t: VOICE_STATUS_PACKET_TYPE,
    m: voiceState.manualMicMuted ? 1 : 0,
    d: voiceState.manualDeafened ? 1 : 0,
    ts: Date.now(),
  });

  let bytes = null;
  try {
    bytes = new TextEncoder().encode(payload);
  } catch {
    return;
  }

  publishVoiceStatusPacket(bytes).catch(() => {});

  if (identity) {
    setRemoteVoiceStatus(identity, voiceState.manualMicMuted, voiceState.manualDeafened);
  }
}

export function broadcastLocalVoiceAnimation(force = false) {
  const identity = String(voiceState.activeRoom?.localParticipant?.identity || '').trim();
  const animation = getLocalVoiceAnimation();
  if (!identity && !force) return;

  const payload = JSON.stringify({
    t: VOICE_ANIMATION_PACKET_TYPE,
    a: animation,
    identity,
    ts: Date.now(),
  });

  let bytes = null;
  try {
    bytes = new TextEncoder().encode(payload);
  } catch {
    return;
  }

  publishVoiceAnimationPacket(bytes).catch(() => {});

  if (identity) {
    setRemoteVoiceAnimation(identity, animation);
  }
}

export function rebroadcastLocalVoiceState() {
  try {
    broadcastLocalSpeakingState(true);
  } catch {}
  try {
    broadcastLocalVoiceStatus(true);
  } catch {}
  try {
    broadcastLocalVoiceAnimation(true);
  } catch {}
}

export function applyReceivedSpeakingPacket(payload, participant) {
  const text = decodeDataPayload(payload);
  if (!text) return false;

  let obj = null;
  try {
    obj = JSON.parse(text);
  } catch {
    return false;
  }

  if (!obj || obj.t !== SPEAKING_PACKET_TYPE) return false;

  const identity = String((participant && participant.identity) || obj.identity || '').trim();
  if (!identity) return false;

  const speaking = !!obj.s;
  const audioLevel = Number(obj.l);

  setRemoteSpeakingState(identity, speaking, Number.isFinite(audioLevel) ? audioLevel : 0);
  return true;
}

export function applyReceivedVoiceStatusPacket(payload, participant) {
  const text = decodeDataPayload(payload);
  if (!text) return false;

  let obj = null;
  try {
    obj = JSON.parse(text);
  } catch {
    return false;
  }

  if (!obj || obj.t !== VOICE_STATUS_PACKET_TYPE) return false;

  const identity = String((participant && participant.identity) || obj.identity || '').trim();
  if (!identity) return false;

  setRemoteVoiceStatus(identity, !!obj.m, !!obj.d);
  return true;
}

export function applyReceivedVoiceAnimationPacket(payload, participant) {
  const text = decodeDataPayload(payload);
  if (!text) return false;

  let obj = null;
  try {
    obj = JSON.parse(text);
  } catch {
    return false;
  }

  if (!obj || obj.t !== VOICE_ANIMATION_PACKET_TYPE) return false;

  const identity = String((participant && participant.identity) || obj.identity || '').trim();
  if (!identity) return false;

  setRemoteVoiceAnimation(identity, obj.a || DEFAULT_VOICE_ANIMATION);
  return true;
}

export function updateLocalSpeakingNow(next, level = voiceState.localSpeakingLevel, db = voiceState.localSpeakingDb) {
  const speaking = !!next;
  const safeLevel = Number(level);
  const safeDb = Number(db);

  voiceState.localSpeakingNow = speaking;
  voiceState.localSpeakingLevel = Number.isFinite(safeLevel) ? safeLevel : 0;
  voiceState.localSpeakingDb = Number.isFinite(safeDb) ? safeDb : -100;

  broadcastLocalSpeakingState(false);
}

export function getLocalSpeakingNow() {
  return !!voiceState.localSpeakingNow;
}

export function getLocalSpeakingLevel() {
  return Number.isFinite(voiceState.localSpeakingLevel) ? voiceState.localSpeakingLevel : 0;
}

export function getLocalSpeakingDb() {
  return Number.isFinite(voiceState.localSpeakingDb) ? voiceState.localSpeakingDb : -100;
}